/**
 * Cost basis of sales: the arithmetic behind COST(). Pure numbers in, pure numbers out — the
 * formula engine (`language.ts`) reads the cells, coerces them and orders the rows, then hands
 * the transactions here. Kept apart so the lot bookkeeping can be read and tested on its own.
 *
 * One pass over the transactions in processing order, one book per asset:
 *   FIFO / LIFO  a list of lots (quantity, unit cost); a sale consumes the oldest / newest first.
 *   WAC          held quantity and its total cost; a sale takes its share of the moving average.
 * Each step is O(1) amortized (FIFO keeps a head index instead of shifting), so a sheet of n
 * rows costs O(n) here plus the caller's sort.
 */

export type CostMethod = 'FIFO' | 'LIFO' | 'WAC'

export interface CostTransaction {
  /** Normalized asset key: rows with the same key share one book. */
  readonly asset: string
  /** Positive buys, negative sells. Never 0 (the caller skips those rows). */
  readonly quantity: number
  /** Total cost of a purchase. Ignored for a sale. */
  readonly totalCost: number
}

/**
 * Per transaction: the total cost of the units a sale took off the books, `'insufficient'` when
 * it sold more than was held (the book is emptied all the same: the units it could cover are
 * gone), or `undefined` for a purchase.
 */
export type CostOutcome = number | 'insufficient' | undefined

interface Lot {
  quantity: number
  unitCost: number
}

interface Book {
  lots: Lot[]
  /** First live lot for FIFO; LIFO pops from the end and never moves it. */
  head: number
  /** WAC only. */
  quantity: number
  totalCost: number
}

/** Below this a remaining quantity is a float residue (0.1 + 0.2 - 0.3), not a holding. */
const EPSILON = 1e-9
const negligible = (left: number, scale: number): boolean => Math.abs(left) <= EPSILON * Math.max(1, Math.abs(scale))

/**
 * `transactions[i]` is row i (`null` when the row is not a transaction); `sequence` lists the row
 * indexes in the order they happened. The result is aligned with `transactions`.
 */
export function costBasis(
  transactions: readonly (CostTransaction | null)[],
  sequence: readonly number[],
  method: CostMethod,
): CostOutcome[] {
  const out: CostOutcome[] = new Array<CostOutcome>(transactions.length).fill(undefined)
  const books = new Map<string, Book>()
  for (const index of sequence) {
    const tx = transactions[index]
    if (tx === null || tx === undefined) continue
    let book = books.get(tx.asset)
    if (book === undefined) books.set(tx.asset, (book = { lots: [], head: 0, quantity: 0, totalCost: 0 }))

    if (tx.quantity > 0) {
      if (method === 'WAC') {
        book.quantity += tx.quantity
        book.totalCost += tx.totalCost
      } else {
        book.lots.push({ quantity: tx.quantity, unitCost: tx.totalCost / tx.quantity })
      }
      continue
    }

    const sold = -tx.quantity
    out[index] = method === 'WAC' ? sellAverage(book, sold) : sellLots(book, sold, method)
  }
  return out
}

function sellAverage(book: Book, sold: number): CostOutcome {
  if (sold > book.quantity && !negligible(sold - book.quantity, sold)) {
    book.quantity = 0
    book.totalCost = 0
    return 'insufficient'
  }
  const cost = book.quantity === 0 ? 0 : book.totalCost * (sold / book.quantity)
  book.quantity -= sold
  book.totalCost -= cost
  if (negligible(book.quantity, sold)) {
    book.quantity = 0
    book.totalCost = 0
  }
  return cost
}

function sellLots(book: Book, sold: number, method: 'FIFO' | 'LIFO'): CostOutcome {
  let left = sold
  let cost = 0
  const { lots } = book
  while (!negligible(left, sold) && book.head < lots.length) {
    const lot = method === 'FIFO' ? lots[book.head]! : lots[lots.length - 1]!
    const take = Math.min(lot.quantity, left)
    cost += take * lot.unitCost
    left -= take
    lot.quantity -= take
    if (negligible(lot.quantity, take)) {
      if (method === 'FIFO') book.head++
      else lots.pop()
    }
  }
  // Drop the consumed prefix once it outweighs the live lots, so a long FIFO history does not
  // keep every lot it ever had.
  if (book.head > 32 && book.head * 2 > lots.length) {
    lots.splice(0, book.head)
    book.head = 0
  }
  return negligible(left, sold) ? cost : 'insufficient'
}
